import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { 
  X, 
  Save, 
  Loader, 
  MessageSquare,
  HelpCircle,
  Tag,
  Eye,
  Settings,
  Sparkles,
  Plus,
  CheckCircle2,
  BookOpen
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Switch } from '../ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { AdminFAQ } from '../../services/contentService';

interface CreateFAQModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (faqData: Omit<AdminFAQ, 'id' | 'createdAt' | 'updatedAt'>) => Promise<void>;
  existingFAQs?: AdminFAQ[];
}

const defaultCategories = [
  { value: 'General', emoji: '💬', color: 'bg-blue-100 text-blue-700' },
  { value: 'Orders & Shipping', emoji: '📦', color: 'bg-green-100 text-green-700' },
  { value: 'Returns & Refunds', emoji: '↩️', color: 'bg-orange-100 text-orange-700' },
  { value: 'Products', emoji: '🛍️', color: 'bg-purple-100 text-purple-700' },
  { value: 'Account', emoji: '👤', color: 'bg-indigo-100 text-indigo-700' },
  { value: 'Payment', emoji: '💳', color: 'bg-pink-100 text-pink-700' },
  { value: 'Technical Support', emoji: '🔧', color: 'bg-red-100 text-red-700' },
  { value: 'Policies', emoji: '📋', color: 'bg-gray-100 text-gray-700' }
];

export function CreateFAQModal({ isOpen, onClose, onSubmit, existingFAQs = [] }: CreateFAQModalProps) {
  const [formData, setFormData] = useState({
    question: '',
    answer: '',
    category: 'General',
    isPublished: true,
    order: existingFAQs.length + 1,
    isHelpful: 0,
    isNotHelpful: 0,
    views: 0
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [customCategory, setCustomCategory] = useState('');
  const [useCustomCategory, setUseCustomCategory] = useState(false);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.question.trim()) {
      newErrors.question = 'Question is required';
    }

    if (!formData.answer.trim()) {
      newErrors.answer = 'Answer is required';
    }

    if (useCustomCategory && !customCategory.trim()) {
      newErrors.customCategory = 'Custom category is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      const faqData: Omit<AdminFAQ, 'id' | 'createdAt' | 'updatedAt'> = {
        question: formData.question.trim(),
        answer: formData.answer.trim(),
        category: useCustomCategory ? customCategory.trim() : formData.category,
        isPublished: formData.isPublished,
        order: formData.order,
        isHelpful: formData.isHelpful,
        isNotHelpful: formData.isNotHelpful,
        views: formData.views
      };

      await onSubmit(faqData);
      toast.success('FAQ created successfully');
      handleClose();
    } catch (error) {
      console.error('Error creating FAQ:', error);
      toast.error('Failed to create FAQ');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setFormData({
      question: '',
      answer: '',
      category: 'General',
      isPublished: true,
      order: existingFAQs.length + 1,
      isHelpful: 0,
      isNotHelpful: 0,
      views: 0
    });
    setCustomCategory('');
    setUseCustomCategory(false);
    setErrors({});
    onClose();
  };

  // Get existing categories from FAQs
  const existingCategories = Array.from(new Set(existingFAQs.map(faq => faq.category)));
  const allCategories = Array.from(new Set([...defaultCategories.map(c => c.value), ...existingCategories]));

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 bg-black/60 backdrop-blur-md z-[10001] flex items-center justify-center p-4" style={{ margin: 0, padding: '1rem', top: 0, left: 0, right: 0, bottom: 0 }}>
        <motion.div
          initial={{ opacity: 0, scale: 0.9, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.9, y: 20 }}
          transition={{ 
            type: "spring",
            duration: 0.5,
            bounce: 0.3 
          }}
          className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col overflow-hidden"
        >
          {/* Enhanced Header */}
          <div className="relative bg-gradient-to-r from-blue-600 to-blue-500 p-6 flex-shrink-0">
            <div className="absolute inset-0 bg-gradient-to-r from-blue-600/20 to-transparent"></div>
            <div className="relative flex items-center justify-between">
              <div className="flex items-center gap-4">
                <motion.div 
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.2, type: "spring", bounce: 0.6 }}
                  className="h-12 w-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center border border-white/30"
                >
                  <MessageSquare className="h-6 w-6 text-white" />
                </motion.div>
                <div>
                  <motion.h2 
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.1 }}
                    className="text-2xl font-bold text-white"
                  >
                    Create FAQ
                  </motion.h2>
                  <motion.p 
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.2 }}
                    className="text-white/80 text-sm"
                  >
                    Help customers with frequently asked questions
                  </motion.p>
                </div>
              </div>
              <motion.div
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
              >
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={handleClose}
                  className="text-white hover:bg-white/20 h-10 w-10 rounded-full"
                >
                  <X className="h-5 w-5" />
                </Button>
              </motion.div>
            </div>
          </div>

          {/* Enhanced Form */}
          <div className="p-8 space-y-8 overflow-y-auto flex-1 bg-gradient-to-b from-gray-50/50 to-white">
            {/* Question */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="space-y-3"
            >
              <Label htmlFor="question" className="text-base font-semibold text-gray-900 flex items-center gap-2">
                <HelpCircle className="h-4 w-4 text-blue-600" />
                Question *
              </Label>
              <Input
                id="question"
                placeholder="What would customers commonly ask about?"
                value={formData.question}
                onChange={(e) => setFormData({ ...formData, question: e.target.value })}
                className={`transition-all duration-200 ${
                  errors.question 
                    ? 'border-red-300 focus:border-red-500 focus:ring-red-200' 
                    : 'border-gray-200 focus:border-blue-600 focus:ring-blue-600/20'
                } rounded-xl text-base p-4 h-12 shadow-sm hover:shadow-md focus:shadow-lg`}
              />
              {errors.question && (
                <motion.p 
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  className="text-sm text-red-600 flex items-center gap-1"
                >
                  <div className="w-1 h-1 bg-red-600 rounded-full"></div>
                  {errors.question}
                </motion.p>
              )}
            </motion.div>

            {/* Answer */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="space-y-3"
            >
              <Label htmlFor="answer" className="text-base font-semibold text-gray-900 flex items-center gap-2">
                <BookOpen className="h-4 w-4 text-blue-600" />
                Answer *
              </Label>
              <Textarea
                id="answer"
                placeholder="Provide a clear, helpful answer that addresses the question completely..."
                value={formData.answer}
                onChange={(e) => setFormData({ ...formData, answer: e.target.value })}
                className={`transition-all duration-200 resize-none ${
                  errors.answer 
                    ? 'border-red-300 focus:border-red-500 focus:ring-red-200' 
                    : 'border-gray-200 focus:border-blue-600 focus:ring-blue-600/20'
                } rounded-xl text-base p-4 min-h-[120px] shadow-sm hover:shadow-md focus:shadow-lg`}
                rows={4}
              />
              {errors.answer && (
                <motion.p 
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  className="text-sm text-red-600 flex items-center gap-1"
                >
                  <div className="w-1 h-1 bg-red-600 rounded-full"></div>
                  {errors.answer}
                </motion.p>
              )}
            </motion.div>

            {/* Category Selection */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="space-y-4"
            >
              <Label className="text-base font-semibold text-gray-900 flex items-center gap-2">
                <Tag className="h-4 w-4 text-blue-600" />
                Category & Organization
              </Label>
              
              <div className="bg-white rounded-xl p-6 border border-gray-100 shadow-sm space-y-4">
                <div className="flex items-center gap-3">
                  <motion.div whileTap={{ scale: 0.95 }}>
                    <Switch
                      checked={useCustomCategory}
                      onCheckedChange={setUseCustomCategory}
                      className="data-[state=checked]:bg-blue-600"
                    />
                  </motion.div>
                  <Label className="text-sm font-medium text-gray-900">Create custom category</Label>
                </div>

                <AnimatePresence mode="wait">
                  {useCustomCategory ? (
                    <motion.div
                      key="custom"
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      className="space-y-2"
                    >
                      <Label htmlFor="customCategory" className="text-sm font-medium text-gray-900">
                        Custom Category Name *
                      </Label>
                      <Input
                        id="customCategory"
                        placeholder="Enter new category name"
                        value={customCategory}
                        onChange={(e) => setCustomCategory(e.target.value)}
                        className={`h-12 rounded-xl ${
                          errors.customCategory ? 'border-red-300 focus:border-red-500' : 'focus:border-blue-600'
                        }`}
                      />
                      {errors.customCategory && (
                        <motion.p 
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          className="text-sm text-red-600"
                        >
                          {errors.customCategory}
                        </motion.p>
                      )}
                    </motion.div>
                  ) : (
                    <motion.div
                      key="existing"
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      className="space-y-3"
                    >
                      <Label className="text-sm font-medium text-gray-900">Select Category</Label>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                        {defaultCategories.map((category) => (
                          <motion.div
                            key={category.value}
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                            onClick={() => setFormData({ ...formData, category: category.value })}
                            className={`p-3 rounded-lg border-2 cursor-pointer transition-all duration-200 ${
                              formData.category === category.value
                                ? 'border-blue-600 bg-blue-50'
                                : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                            }`}
                          >
                            <div className="flex items-center gap-2">
                              <span className="text-lg">{category.emoji}</span>
                              <span className="text-sm font-medium text-gray-900">{category.value}</span>
                            </div>
                            {formData.category === category.value && (
                              <motion.div
                                initial={{ scale: 0 }}
                                animate={{ scale: 1 }}
                                className="absolute top-1 right-1 w-5 h-5 bg-blue-600 rounded-full flex items-center justify-center"
                              >
                                <CheckCircle2 className="w-3 h-3 text-white" />
                              </motion.div>
                            )}
                          </motion.div>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </motion.div>

            {/* Publishing Options */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
              className="space-y-4"
            >
              <Label className="text-base font-semibold text-gray-900 flex items-center gap-2">
                <Settings className="h-4 w-4 text-blue-600" />
                Publishing Options
              </Label>
              
              <div className="bg-white rounded-xl p-6 border border-gray-100 shadow-sm">
                <div className="flex items-center justify-between group">
                  <div className="space-y-1">
                    <Label className="text-base font-medium text-gray-900">Publish Immediately</Label>
                    <p className="text-sm text-gray-500">Make this FAQ visible to customers right away</p>
                  </div>
                  <motion.div whileTap={{ scale: 0.95 }}>
                    <Switch
                      checked={formData.isPublished}
                      onCheckedChange={(checked) => setFormData({ ...formData, isPublished: checked })}
                      className="data-[state=checked]:bg-blue-600"
                    />
                  </motion.div>
                </div>
              </div>
            </motion.div>

            {/* Live Preview */}
            <AnimatePresence>
              {formData.question && formData.answer && (
                <motion.div 
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ delay: 0.7 }}
                  className="space-y-3"
                >
                  <Label className="text-base font-semibold text-gray-900 flex items-center gap-2">
                    <Eye className="h-4 w-4 text-blue-600" />
                    Live Preview
                  </Label>
                  <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-100">
                    <div className="space-y-4">
                      <div className="flex items-start gap-3">
                        <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                          <HelpCircle className="w-4 h-4 text-white" />
                        </div>
                        <div className="flex-1">
                          <h3 className="font-semibold text-gray-900 mb-2">{formData.question}</h3>
                          <p className="text-gray-700 leading-relaxed">{formData.answer}</p>
                          <div className="flex items-center gap-4 mt-4 pt-4 border-t border-blue-200">
                            <div className={`px-3 py-1 rounded-full text-xs font-medium ${
                              defaultCategories.find(c => c.value === formData.category)?.color || 'bg-gray-100 text-gray-700'
                            }`}>
                              {defaultCategories.find(c => c.value === formData.category)?.emoji} {useCustomCategory ? customCategory : formData.category}
                            </div>
                            <div className={`px-2 py-1 rounded text-xs font-medium ${
                              formData.isPublished ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                            }`}>
                              {formData.isPublished ? 'Published' : 'Draft'}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Enhanced Footer */}
          <div className="p-6 bg-gray-50 border-t border-gray-100 flex-shrink-0">
            <div className="flex items-center justify-end gap-4">
              <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                <Button 
                  variant="outline" 
                  onClick={handleClose} 
                  disabled={isSubmitting}
                  className="px-6 h-11 rounded-xl font-medium"
                >
                  Cancel
                </Button>
              </motion.div>
              <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                <Button 
                  onClick={handleSubmit}
                  disabled={isSubmitting || !formData.question.trim() || !formData.answer.trim()}
                  className="bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-700 hover:to-blue-600 text-white px-8 h-11 rounded-xl font-semibold shadow-lg shadow-blue-600/25"
                >
                  {isSubmitting ? (
                    <>
                      <Loader className="w-4 h-4 mr-2 animate-spin" />
                      Creating FAQ...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      Create FAQ
                    </>
                  )}
                </Button>
              </motion.div>
            </div>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
} 